import { useParams } from "react-router";
import {
  Building2,
  Calendar,
  Clock,
  Crown,
  Database,
  DoorOpen,
  FlaskConical,
  Layers,
  Plug,
  ScanLine,
  ShieldCheck,
  FileDown,
  Stethoscope,
  Tag,
  Trash2,
  UserRound,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { usePermissao } from "@/lib/use-permissao";
import { useSetPageHeader } from "@/lib/page-header";
import { useIsMobile } from "@/lib/use-media-query";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { CONFIG_AGENDA_TOUR_STEPS, CONFIG_IMPORTAR_TOUR_STEPS, CONFIG_ORGANIZACAO_TOUR_STEPS } from "@/lib/product-tour/page-tour-steps";
import { PaginaComSecoes, type Secao } from "@/components/layout/PaginaComSecoes";
import { DadosSecao } from "@/components/settings/secoes/DadosSecao";
import { ExpedienteSecao } from "@/components/settings/secoes/ExpedienteSecao";
import { SalasSecao } from "@/components/settings/secoes/SalasSecao";
import { AreaDeAtuacaoSecao } from "@/components/settings/secoes/AreaDeAtuacaoSecao";
import { EspecialidadesSecao } from "@/components/settings/secoes/EspecialidadesSecao";
import { PermissoesSecao } from "@/components/settings/secoes/PermissoesSecao";
import { PropriedadeSecao } from "@/components/settings/secoes/PropriedadeSecao";
import { RegistroDeExportacoesSecao } from "@/components/settings/secoes/RegistroDeExportacoesSecao";
import { LaboratorioSecao, RadiologiaSecao } from "@/components/settings/secoes/PerfisDaOrganizacao";
import { EmBreveSecao } from "@/components/settings/secoes/EmBreveSecao";
import { EtiquetasSecao } from "@/components/settings/etiquetas/EtiquetasSecao";
import { SettingsAgenda } from "./SettingsAgenda";
import { SettingsPatients } from "./SettingsPatients";
import { SettingsImport } from "./SettingsImport";
import { ExclusaoSecao } from "@/components/settings/exclusao/ExclusaoSecao";

const i = (Icone: typeof Building2) => <Icone className="h-4 w-4" />;

const TOUR_POR_SECAO: Record<string, typeof CONFIG_ORGANIZACAO_TOUR_STEPS> = {
  dados: CONFIG_ORGANIZACAO_TOUR_STEPS,
  agenda: CONFIG_AGENDA_TOUR_STEPS,
  importar: CONFIG_IMPORTAR_TOUR_STEPS,
};

/**
 * Configurações da organização ativa, numa página só com seções — só pra quem administra
 * (`settings.manage`; dono e sócio sempre). O que é da pessoa mora em Minha conta.
 */
export function Configuracoes() {
  const { secao } = useParams();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const podeOrganizacao = usePermissao("settings.manage");
  // Cadastro, anamnese e ficha têm permissão própria: quem só tem ela entra e vê só essa seção.
  const podePacientes = usePermissao("patients.configure");
  const podeConfigurar = podeOrganizacao || podePacientes;
  const org = user?.activeOrganization;
  const tipos = org?.types ?? [];
  const pessoal = !!org?.isPersonal;
  const clinica = tipos.length === 0 || tipos.includes("DENTAL_OFFICE");

  const todas: Secao[] = [
    { chave: "dados", grupo: "Organização", rotulo: "Dados", descricao: "Nome, CNPJ, contato, endereço e prazo", icone: i(Building2), cor: "#2457ff", conteudo: <DadosSecao /> },
    { chave: "expediente", grupo: "Organização", rotulo: "Expediente", descricao: "Quando a organização está aberta", icone: i(Clock), cor: "#0891b2", conteudo: <ExpedienteSecao /> },
    { chave: "atuacao", grupo: "Organização", rotulo: "Área de atuação", descricao: "Clínica, laboratório, radiologia — o que aparece no menu", icone: i(Layers), cor: "#6366f1", conteudo: <AreaDeAtuacaoSecao /> },
    ...(clinica && !pessoal
      ? [{ chave: "especialidades", grupo: "Organização", rotulo: "Especialidades", descricao: "Especialidades ativas e seus serviços", icone: i(Stethoscope), cor: "#0ea5e9", conteudo: <EspecialidadesSecao /> }]
      : []),
    ...(tipos.includes("DENTAL_LAB")
      ? [{ chave: "perfil-publico", grupo: "Organização", rotulo: "Perfil público", descricao: "Como as clínicas veem o laboratório: nome, logo, busca e link do catálogo", icone: i(FlaskConical), cor: "#d946ef", conteudo: <LaboratorioSecao /> }]
      : []),
    ...(tipos.includes("RADIOLOGY_CENTER")
      ? [{ chave: "radiologia", grupo: "Organização", rotulo: "Radiologia", descricao: "Serviços e endereço de atendimento", icone: i(ScanLine), cor: "#f59e0b", conteudo: <RadiologiaSecao /> }]
      : []),
    ...(clinica
      ? [
          { chave: "agenda", grupo: "Funcionamento", rotulo: "Agenda", descricao: "Horários, visual, lembretes e regras", icone: i(Calendar), cor: "#0ea5e9", conteudo: <SettingsAgenda embutido />, semMoldura: true },
          { chave: "salas", grupo: "Funcionamento", rotulo: "Salas", descricao: "Cadeiras de atendimento e a sala de cada profissional", icone: i(DoorOpen), cor: "#7c3aed", conteudo: <SalasSecao /> },
          ...(podePacientes ? [{ chave: "pacientes", grupo: "Funcionamento", rotulo: "Pacientes", descricao: "Cadastro, anamnese e ficha", icone: i(UserRound), cor: "#14b8a6", conteudo: <SettingsPatients embutido />, semMoldura: true }] : []),
        ]
      : []),
    ...(tipos.includes("DENTAL_LAB")
      ? [{ chave: "etiquetas", grupo: "Funcionamento", rotulo: "Etiquetas", descricao: "Campos, ordem e tamanho das etiquetas de produção e entrega", icone: i(Tag), cor: "#d946ef", conteudo: <EtiquetasSecao /> }]
      : []),
    ...(!pessoal
      ? [{ chave: "permissoes", grupo: "Equipe e acesso", rotulo: "Permissões", descricao: "O que cada papel pode acessar", icone: i(ShieldCheck), cor: "#10b981", conteudo: <PermissoesSecao /> }]
      : []),
    // Quem exportou o quê: do proprietário e do sócio, como a matriz de permissões que libera exportar.
    ...(!pessoal && (org?.role === "OWNER" || org?.role === "PARTNER")
      ? [{ chave: "exportacoes", grupo: "Equipe e acesso", rotulo: "Registro de exportações", descricao: "Quem baixou planilhas e exportou dados", icone: i(FileDown), cor: "#0f766e", conteudo: <RegistroDeExportacoesSecao /> }]
      : []),
    ...(!pessoal && org?.role === "OWNER"
      ? [
          { chave: "propriedade", grupo: "Equipe e acesso", rotulo: "Propriedade", descricao: "Transferir a propriedade da organização", icone: i(Crown), cor: "#b45309", conteudo: <PropriedadeSecao /> },
          { chave: "exclusao", grupo: "Equipe e acesso", rotulo: "Excluir organização", descricao: "Apagar a organização e os dados dela", icone: i(Trash2), cor: "#dc2626", conteudo: <ExclusaoSecao /> },
        ]
      : []),
    { chave: "importar", grupo: "Dados", rotulo: "Importar", descricao: "Trazer dados de outra plataforma", icone: i(Database), cor: "#64748b", conteudo: <SettingsImport embutido />, semMoldura: true },
    { chave: "integracoes", grupo: "Dados", rotulo: "Integrações", descricao: "WhatsApp, Google Agenda, pagamentos", icone: i(Plug), cor: "#64748b", conteudo: <EmBreveSecao /> },
  ];

  const secoes = podeOrganizacao ? todas : todas.filter((s) => s.chave === "pacientes");
  const ativa = secoes.find((s) => s.chave === secao);
  useTourDaPagina(podeConfigurar ? (TOUR_POR_SECAO[secao ?? ""] ?? []) : []);
  useSetPageHeader(
    {
      title: isMobile ? (ativa?.rotulo ?? "Configurações") : ativa ? `Configurações · ${ativa.rotulo}` : "Configurações",
      subtitle: isMobile ? undefined : org?.name,
    },
    [isMobile, ativa?.chave, org?.name],
  );

  if (!podeConfigurar) {
    return (
      <div className="mx-auto max-w-md rounded-xl border border-zinc-200 bg-white px-6 py-10 text-center shadow-sm dark:border-border dark:bg-card">
        <ShieldCheck className="mx-auto h-8 w-8 text-zinc-300" />
        <p className="mt-3 text-sm font-semibold text-zinc-900 dark:text-foreground">Só quem administra a organização</p>
        <p className="mt-1 text-[12.5px] text-zinc-500 dark:text-muted-foreground">
          As configurações de {org?.name ?? "organização"} ficam com o dono e com quem tem a permissão de configurar.
          As suas preferências estão em Minha conta.
        </p>
      </div>
    );
  }

  return <PaginaComSecoes base="/configuracoes" titulo="Configurações" secoes={secoes} />;
}
